// =============================================================================
// FailedNotificationsList — admin view of notification emails that failed to send
// =============================================================================
// Reads the `email_send_failed` rows that sendEmail (utils/actions.ts) writes to
// audit_logs on any send failure, and shows them as a table so an admin can see,
// in one place, which shelter/invoicing/assignee notifications didn't go out.
//
// Read-only. The underlying request data is never lost when an email fails — only
// the notification. Each failure is also alerted to the System Alert Email Address
// (see SystemSettingsAdmin + the send-email Edge Function "system_alert" type).
// =============================================================================
import React from "react";
import { listFailedNotifications } from "../../data/auditLogs";
import { GLYPH } from "../../utils/glyphs";

// Known live email types. An unknown type falls back to the raw string below, so a
// historical row for a retired type (e.g. the removed `cancellation_invoicing`,
// finding B6) still renders — it just shows the raw type instead of a label.
const TYPE_LABELS = {
  comment_to_shelter: `Comment ${GLYPH.arrowRight} DFV Service`,
  file_to_shelter: `Document ${GLYPH.arrowRight} DFV Service`,
  comment_to_invoicing: `Comment ${GLYPH.arrowRight} Invoicing`,
  new_invoice: `Invoice ${GLYPH.arrowRight} Zephyr team`,
  task_assigned: "Task assigned",
};

function parseDetails(details) {
  if (!details) return {};
  try {
    return typeof details === "string" ? JSON.parse(details) : details;
  } catch (_e) {
    return {};
  }
}

export function FailedNotificationsList() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  function load() {
    setLoading(true);
    listFailedNotifications().then(function (rows) {
      setLoading(false);
      if (rows) setRows(rows);
    });
  }

  React.useEffect(function () {
    load();
  }, []);

  const TH =
    "py-[10px] px-[12px] font-bold text-zephyr-muted text-[11px] uppercase text-left whitespace-nowrap";
  const TD = "py-[9px] px-[12px] text-[12px] text-zephyr-text align-top";

  return (
    <div className="mt-10 max-w-[860px]">
      <div className="flex justify-between items-center mb-2">
        <h3 className="m-0 text-[15px] font-bold text-zephyr-text">Recent failed notifications</h3>
        <button
          onClick={load}
          className="bg-zephyr-teal text-white border-none rounded-[7px] py-[6px] px-[14px] text-[13px] font-semibold cursor-pointer"
          style={{ fontFamily: "inherit" }}
        >
          Refresh
        </button>
      </div>
      <p className="m-0 mb-3 text-[13px] text-zephyr-muted">
        Notification emails that could not be sent. The underlying action (comment, invoice,
        document, task) was still saved — only the email didn't go out. Each failure is also alerted
        to the System Alert Email Address above. Showing the 50 most recent.
      </p>
      {loading ? (
        <p className="text-zephyr-muted text-sm">Loading...</p>
      ) : rows.length === 0 ? (
        <p className="text-[13px] font-semibold text-[#2E7D32]">
          No failed notifications — all emails have sent successfully. {GLYPH.check}
        </p>
      ) : (
        <div className="border border-zephyr-border rounded-[10px] overflow-hidden">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-zephyr-off-white">
                <th className={TH}>Date</th>
                <th className={TH}>Type</th>
                <th className={TH}>Recipient</th>
                <th className={TH}>Request</th>
                <th className={TH}>Error</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(function (row, idx) {
                const d = parseDetails(row.details);
                return (
                  <tr
                    key={row.id}
                    className={`${idx % 2 === 0 ? "bg-white" : "bg-zephyr-off-white"} border-t border-zephyr-border`}
                  >
                    <td className={`${TD} text-zephyr-muted whitespace-nowrap`}>
                      {new Date(row.created_at).toLocaleString("en-AU", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td className={`${TD} whitespace-nowrap`}>
                      {TYPE_LABELS[d.emailType] || d.emailType || "—"}
                    </td>
                    <td className={TD}>{d.recipient || "—"}</td>
                    <td className={`${TD} whitespace-nowrap`}>{d.refNo || "—"}</td>
                    <td className={`${TD} text-[#C62828] max-w-[280px] break-words`}>
                      {d.error || "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
